import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Form, Spinner, Tab, Tabs } from 'react-bootstrap'
import { Dialog } from 'primereact/dialog'
import { api } from '../../../../../auth'
import { useAppSelector } from '../../../../../store/hooks'
import { Language } from '../../../../../features/security/security.slice'
import HtmlEditor from '../../../../common/forms/fields/HtmlEditor'

interface EmailTemplate {
    id: number
    name: string
    category: string
}

interface SendEmailDialogProps {
    show: boolean
    onHide: () => void
    onSent: () => void
}

function extractErrorMessage(data: any): string {
    if (!data) return 'Failed to send email'
    const errors = data.errors
    if (!errors) return data.error || data.message || 'Failed to send email'
    if (Array.isArray(errors)) return errors.join(', ')
    if (typeof errors === 'object') {
        return Object.values(errors)
            .flat()
            .join(', ')
    }
    return String(errors)
}

const SendEmailDialog: React.FC<SendEmailDialogProps> = ({
    show,
    onHide,
    onSent,
}) => {
    const axiosPrivate = api
    const initData = useAppSelector((state: any) => state.security.initData)
    const languagesData = initData?.languages?.data || {}

    const [templates, setTemplates] = useState<EmailTemplate[]>([])
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
    const [fields, setFields] = useState<Record<string, string>>({})
    const [recipientFilter, setRecipientFilter] = useState('active')
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [errMsg, setErrMsg] = useState('')

    const loadTemplates = useCallback(async () => {
        setIsLoadingTemplates(true)
        try {
            const response = await axiosPrivate.get(
                '/subscriptions/email-templates'
            )
            if (response.data?.success) {
                setTemplates(response.data.data.templates || [])
            }
        } catch {
            // Non-critical
        } finally {
            setIsLoadingTemplates(false)
        }
    }, [axiosPrivate])

    useEffect(() => {
        if (show) {
            setSelectedTemplateId('')
            setFields({})
            setRecipientFilter('active')
            setErrMsg('')
            loadTemplates()
        }
    }, [show, loadTemplates])

    const handleTemplateChange = async (templateId: string) => {
        setSelectedTemplateId(templateId)
        if (!templateId) {
            const cleared = { ...fields }
            Object.keys(languagesData).forEach((locale) => {
                const lang: Language = languagesData[locale]
                cleared[`body_${lang.id}`] = ''
            })
            setFields(cleared)
            return
        }

        const template = templates.find(
            (t) => t.id === parseInt(templateId, 10)
        )
        if (template) {
            const updated = { ...fields }

            try {
                const res = await axiosPrivate.get(
                    `/subscriptions/email-templates/${template.id}`
                )
                if (res.data?.success && res.data.data?.defaults) {
                    Object.entries(res.data.data.defaults).forEach(
                        ([key, value]) => {
                            updated[key] = value as string
                        }
                    )
                }
            } catch {
                // Ignore — fields remain empty for manual entry
            }

            setFields(updated)
        }
    }

    const updateField = (key: string, value: string) => {
        setFields((prev) => ({ ...prev, [key]: value }))
    }

    const handleSend = async () => {
        const hasAnySubject = Object.keys(languagesData).some((locale) => {
            const lang: Language = languagesData[locale]
            return fields[`subject_${lang.id}`]?.trim()
        })

        if (!selectedTemplateId && !hasAnySubject) {
            setErrMsg(
                'Please select a template, or provide a subject in at least one language.'
            )
            return
        }

        if (!hasAnySubject) {
            setErrMsg('Subject is required in at least one language.')
            return
        }

        const hasAnyBody = Object.keys(languagesData).some((locale) => {
            const lang: Language = languagesData[locale]
            return fields[`body_${lang.id}`]?.trim()
        })

        if (!hasAnyBody) {
            setErrMsg('Body is required in at least one language.')
            return
        }

        setIsSending(true)
        setErrMsg('')

        try {
            const payload: Record<string, unknown> = {
                ...fields,
                recipient_filter: recipientFilter,
            }

            if (selectedTemplateId) {
                payload.template_id = parseInt(selectedTemplateId, 10)
            }

            const response = await axiosPrivate.post(
                '/subscriptions/send-email',
                payload
            )

            if (response.data?.success) {
                setFields({})
                setRecipientFilter('active')
                setSelectedTemplateId('')
                onSent()
            } else {
                setErrMsg(extractErrorMessage(response.data))
            }
        } catch (err: any) {
            setErrMsg(extractErrorMessage(err?.response?.data))
        } finally {
            setIsSending(false)
        }
    }

    const handleClose = () => {
        setErrMsg('')
        onHide()
    }

    const usingTemplate = !!selectedTemplateId

    return (
        <Dialog
            header="Send Email to Subscribers"
            visible={show}
            onHide={handleClose}
            style={{ width: 'min(90vw, 640px)' }}
            dismissableMask
            closable
            maximizable
            footer={
                <>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSend}
                        disabled={isSending}
                    >
                        {isSending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Sending...
                            </>
                        ) : (
                            'Send Email'
                        )}
                    </Button>
                </>
            }
        >
            {errMsg && <Alert variant="danger">{errMsg}</Alert>}

                <fieldset>
                    <legend>Settings</legend>
                    <Form.Group className="mb-3">
                        <Form.Label>Recipients</Form.Label>
                        <Form.Select
                            value={recipientFilter}
                            onChange={(e) => setRecipientFilter(e.target.value)}
                        >
                            <option value="active">
                                Active subscribers only
                            </option>
                            <option value="all">All subscribers</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Email Template{' '}
                            <small className="text-muted">(optional)</small>
                        </Form.Label>
                        {isLoadingTemplates ? (
                            <div className="py-2">
                                <Spinner animation="border" size="sm" />{' '}
                                <small>Loading templates...</small>
                            </div>
                        ) : (
                            <Form.Select
                                value={selectedTemplateId}
                                onChange={(e) =>
                                    handleTemplateChange(e.target.value)
                                }
                            >
                                <option value="">
                                    — No template (custom content) —
                                </option>
                                {templates.map((tpl) => (
                                    <option key={tpl.id} value={tpl.id}>
                                        {tpl.name}
                                        {tpl.category
                                            ? ` (${tpl.category})`
                                            : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                        {usingTemplate && (
                            <Form.Text className="text-muted">
                                The template signature will be used. Edit the pre-populated content below.
                            </Form.Text>
                        )}
                    </Form.Group>
                </fieldset>

                <Tabs id="sendEmailLangTabs" className="mb-0 mt-3">
                    {Object.keys(languagesData).map((locale: string) => {
                        const lang: Language = languagesData[locale]

                        return (
                            <Tab
                                eventKey={locale}
                                title={lang.name}
                                key={locale}
                                className="fade border-start border-end border-bottom mb-3 p-3 in-tab-content"
                            >
                                <Form.Group className="mb-3">
                                    <Form.Label>Subject</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Email subject"
                                        value={
                                            fields[`subject_${lang.id}`] || ''
                                        }
                                        onChange={(e) =>
                                            updateField(
                                                `subject_${lang.id}`,
                                                e.target.value
                                            )
                                        }
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label>Email body content</Form.Label>
                                    <HtmlEditor
                                        key={`send_body_${lang.id}`}
                                        value={
                                            fields[`body_${lang.id}`] || ''
                                        }
                                        onChange={(val) =>
                                            updateField(
                                                `body_${lang.id}`,
                                                val
                                            )
                                        }
                                        placeholder={`Write content in ${lang.name}...`}
                                    />
                                </Form.Group>
                            </Tab>
                        )
                    })}
                </Tabs>
        </Dialog>
    )
}

export default SendEmailDialog
